import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Recycle, ShoppingCart, Ship,
  Award, BadgeCheck, Globe, Headphones, Wallet, Leaf,
  Factory, Truck, Repeat } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { TaglineMarquee } from "@/components/Extras";
import { PILLARS, SERVICES, WHY_CHOOSE, MATERIALS, RECOVERY_PACKAGE, BUYER_AUDIENCE } from "@/lib/api";

const SLIDES = [
  {
    tag: "NON-PRIME RESIN", accent: "Direct Buyer of", rest: "Non-Prime & Off-Grade Resin",
    sub: "SAYE IMPEX RECYCLING INC. runs a direct-purchase program for post-industrial LDPE, LLDPE, HDPE, PP and PET — off-spec, wide-spec, transitional and purge lots — buying on recurring contracts from manufacturers, converters and molders across North America.",
    img: "/images/hero-facility.png", to: "/services",
  },
  {
    tag: "POST-INDUSTRIAL RECOVERY", accent: "Post-Industrial", rest: "Scrap Recovery Partner",
    sub: "Scheduled recovery programs for corrugators, film converters, printers and packaging operations. OCC, mixed office paper, converter trim and packaging stock lots — placed downstream into verified mills and processors with audit-ready documentation.",
    img: "/images/occ-cardboard.png", to: "/services",
  },
  {
    tag: "PPE & COMPLIANT RECOVERY", accent: "Compliant Recovery", rest: "for Brand Owners & Distributors",
    sub: "End-of-life handling for surplus and expired PPE inventory — masks, gloves, gowns and gowns held by brand owners, distributors and healthcare buyers. Regulation-compliant paperwork and secondary-material routing included.",
    img: "/images/ppe.png", to: "/services",
  },
];

const RECOVERY_ICONS = { factory: Factory, recycle: Recycle, truck: Truck, globe: Globe, repeat: Repeat };
const PILLAR_ICONS = [Recycle, ShoppingCart, Ship];
const WHY_ICONS = { award: Award, "badge-check": BadgeCheck, globe: Globe, headphones: Headphones, wallet: Wallet, leaf: Leaf };

const Home = () => {
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  const go = useCallback((n) => setIdx((p) => (n + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    timer.current = setInterval(() => setIdx((p) => (p + 1) % SLIDES.length), 6000);
    return () => clearInterval(timer.current);
  }, []);

  const reset = () => { clearInterval(timer.current); timer.current = setInterval(() => setIdx((p) => (p + 1) % SLIDES.length), 6000); };
  const slide = SLIDES[idx];

  return (
    <div data-testid="home-page">
      {/* HERO CAROUSEL */}
      <section className="relative min-h-screen bg-navy overflow-hidden flex items-center" data-testid="hero-carousel">
        <AnimatePresence>
          <motion.div key={idx} className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
            <img src={slide.img} alt={slide.tag} className="w-full h-full object-cover" />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        <div className="absolute inset-0 grain" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-28">
          <AnimatePresence mode="wait">
            <motion.div key={idx}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
              <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-6">/ {slide.tag}</div>
              <h1 className="font-display font-black text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                <span className="text-emerald">{slide.accent}</span> {slide.rest}
              </h1>
              <p className="text-white/70 mt-8 max-w-xl text-base md:text-lg leading-relaxed">{slide.sub}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/submit-offer" data-testid="hero-submit-offer"
                  className="group bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-7 py-4 flex items-center gap-2 hover:gap-4 transition-all">
                  Sell Your Material <ArrowRight size={16} />
                </Link>
                <Link to={slide.to} data-testid="hero-learn-more"
                  className="border border-white/30 text-white font-mono text-xs uppercase tracking-widest px-7 py-4 hover:bg-white hover:text-navy transition-colors">
                  Our Buyer Program
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* controls */}
        <button onClick={() => { go(idx - 1); reset(); }} data-testid="hero-prev"
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 border border-white/30 text-white flex items-center justify-center hover:bg-emerald hover:text-navy hover:border-emerald transition-colors">
          <ChevronLeft size={22} />
        </button>
        <button onClick={() => { go(idx + 1); reset(); }} data-testid="hero-next"
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 border border-white/30 text-white flex items-center justify-center hover:bg-emerald hover:text-navy hover:border-emerald transition-colors">
          <ChevronRight size={22} />
        </button>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => { setIdx(i); reset(); }} data-testid={`hero-dot-${i}`}
              className={`h-1.5 transition-all ${i === idx ? "w-10 bg-emerald" : "w-4 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      </section>

      {/* RECOVERY PACKAGE — 5-Point Positioning */}
      <section className="bg-navy border-t border-white/10 py-20 md:py-28" data-testid="recovery-package">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-4">/ THE SAYE PACKAGE</div>
              <FadeUp>
                <h2 className="font-display font-black text-white text-4xl md:text-5xl tracking-tighter leading-none">
                  Manufacturer recovery <span className="text-emerald">& secondary-material buyer.</span>
                </h2>
              </FadeUp>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-white/60 leading-relaxed">
                Five things we do — as a principal buyer, not a broker. Built for producers with
                recurring off-grade streams, not one-off spot deals.
              </p>
            </div>
          </div>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {RECOVERY_PACKAGE.map((r, i) => {
              const Icon = RECOVERY_ICONS[r.icon] || Factory;
              return (
                <StaggerItem key={r.title}>
                  <div className="bg-navy p-7 h-full flex flex-col" data-testid={`recovery-${i}`}>
                    <div className="flex items-center justify-between mb-6">
                      <Icon size={28} className="text-emerald" strokeWidth={1.5} />
                      <span className="font-mono text-xs text-white/30">0{i + 1}</span>
                    </div>
                    <h3 className="font-display font-black text-white text-lg tracking-tight leading-tight">{r.title}</h3>
                    <p className="text-white/60 text-sm mt-3 leading-relaxed">{r.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-offwhite border-b border-navy/10">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4">
          {[["Non-Prime", "& Off-Grade Resin"], ["Recurring", "Contract Buyer"], ["N. American", "Pickup Network"], ["Global", "Downstream Placement"]].map(([n, l], i) => (
            <div key={i} className="p-8 md:p-10 border-r border-navy/10 last:border-r-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r">
              <div className="font-display font-black text-2xl md:text-3xl text-navy tracking-tight">{n}</div>
              <div className="font-mono text-xs uppercase tracking-widest text-navy/50 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <TaglineMarquee />

      {/* ABOUT / WHAT WE DO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-4">/ WHAT WE DO</div>
            <FadeUp>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-navy leading-none">
                Recovery. Purchase. Placement.
              </h2>
            </FadeUp>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <FadeUp delay={0.1}>
              <p className="text-navy/70 text-lg leading-relaxed">
                Based in British Columbia, SAYE IMPEX RECYCLING INC. is a direct buyer of post-industrial
                polymer, paper and PPE streams. We contract on recurring monthly volume, coordinate
                pickup across Canada and the US, and place graded material into verified international
                downstream buyers — one team, one point of contact, end-to-end.
              </p>
            </FadeUp>
          </div>
        </div>

        <Stagger className="border-t border-l border-navy/10 grid md:grid-cols-2">
          {SERVICES.map((s) => (
            <StaggerItem key={s.n}>
              <Link to="/services" className="group block border-b border-r border-navy/10 p-8 md:p-12 hover:bg-navy transition-colors duration-300" data-testid={`home-service-${s.n}`}>
                <div className="flex items-start justify-between">
                  <span className="font-mono text-sm text-emerald">{s.n}</span>
                  <ArrowRight size={20} className="text-navy/30 group-hover:text-emerald group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight text-navy group-hover:text-white transition-colors mt-8">{s.title}</h3>
                <p className="text-navy/60 group-hover:text-white/60 transition-colors mt-3 text-sm leading-relaxed max-w-md">{s.desc}</p>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* WHO WE BUY FROM */}
      <section className="bg-white border-y border-navy/10 py-24 md:py-32" data-testid="buyer-audience">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-4">/ WHO WE BUY FROM</div>
              <FadeUp>
                <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-navy leading-none">
                  Built for producers with <span className="text-emerald">recurring streams.</span>
                </h2>
              </FadeUp>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-navy/60 leading-relaxed">
                If your operation generates post-industrial polymer, paper or packaging on a
                monthly basis, our buyer program is built around your material — not the other
                way around.
              </p>
            </div>
          </div>

          <Stagger className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-px bg-navy/10 border border-navy/10">
            {BUYER_AUDIENCE.map((a, i) => (
              <StaggerItem key={a}>
                <div className="bg-white p-6 h-full flex items-center gap-3" data-testid={`audience-${i}`}>
                  <span className="font-mono text-xs text-emerald shrink-0">/{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display font-bold text-navy tracking-tight text-sm md:text-base">{a}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-offwhite border-y border-navy/10 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-4">/ WHY SAYE IMPEX</div>
          <FadeUp><h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-navy mb-16">Your recovery & secondary-material partner.</h2></FadeUp>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
            {WHY_CHOOSE.map((w) => {
              const Icon = WHY_ICONS[w.icon] || Award;
              return (
                <div key={w.title} className="bg-white p-8 h-full" data-testid={`why-${w.title}`}>
                  <Icon size={28} className="text-emerald mb-6" strokeWidth={1.5} />
                  <h3 className="font-display font-bold text-xl text-navy tracking-tight">{w.title}</h3>
                  <p className="text-navy/60 text-sm mt-2 leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROUDLY CANADIAN */}
      <section className="bg-navy relative grain overflow-hidden" data-testid="canada-band">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 relative z-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-4">/ PROUDLY CANADIAN</div>
            <FadeUp>
              <h2 className="font-display font-black text-white text-3xl md:text-5xl tracking-tighter leading-tight">
                Rooted in British Columbia,<br /><span className="text-emerald">buying across North America.</span>
              </h2>
            </FadeUp>
            <p className="text-white/60 mt-6 max-w-xl leading-relaxed">
              A Canadian buyer working directly with manufacturers, converters and brand owners across
              Canada and the United States — keeping domestic material in a responsible, homegrown
              recovery loop before international downstream placement.
            </p>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex md:justify-end">
            <div className="flex items-center gap-4 border border-white/15 px-8 py-6">
              <Leaf size={40} className="text-emerald" strokeWidth={1.5} />
              <div>
                <div className="font-display font-black text-white text-2xl tracking-tight">Canada</div>
                <div className="font-mono text-xs uppercase tracking-widest text-white/50 mt-1">Coast to Coast</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MATERIALS PREVIEW */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-14">
          <div>
            <div className="font-mono text-xs tracking-[0.3em] text-emerald mb-4">/ MATERIALS WE BUY</div>
            <FadeUp><h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-navy">Priced streams, placed globally.</h2></FadeUp>
          </div>
          <Link to="/products" data-testid="home-view-products" className="font-mono text-xs uppercase tracking-widest text-navy border-b-2 border-emerald pb-1 hover:text-emerald transition-colors">View all materials</Link>
        </div>
        <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {MATERIALS.slice(0, 4).map((m) => (
            <StaggerItem key={m.name}>
              <div className="group relative overflow-hidden" data-testid={`home-material-${m.name}`}>
                <img src={m.img} alt={m.name} className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5">
                  <span className="font-mono text-xs text-emerald">{m.code}</span>
                  <div className="font-display font-bold text-white text-lg leading-tight mt-1 drop-shadow">{m.name}</div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* CTA */}
      <section className="bg-navy relative grain overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 relative z-10">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <FadeUp>
                <h2 className="font-display font-black text-white text-4xl md:text-6xl tracking-tighter leading-none">
                  Have off-grade or<br /><span className="text-emerald">surplus resin?</span>
                </h2>
              </FadeUp>
              <p className="text-white/60 mt-6 max-w-lg">Non-prime, off-spec, wide-spec, transitional, purge or post-industrial recyclable plastic — we buy on recurring contracts with competitive pricing and fast payment.</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link to="/submit-offer" data-testid="cta-submit-offer"
                className="group bg-emerald text-navy font-mono text-sm uppercase tracking-widest px-8 py-5 flex items-center gap-3 hover:gap-5 transition-all">
                Sell Your Material <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
